import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  useGetApproverReport,
  useAIFMUpdateData,
  useGetAIFMJurisdictions,
  useGetAIFMReport,
  useGetJurisdictions,
  useGetLoadAllData,
  useGetReportSchema,
  useUpdateData
} from '../../../../services/report.service';
import { filterArrayByFieldsNotEmptyValue, getDefaultValueByType } from '../utils/functions';
import { useGetFormPFJurisdiction } from '../../../../services/jurisdiction.service';
import toast from 'react-hot-toast';
import { useParams, useSearchParams } from 'react-router-dom';
import commonStore from '../../../../store/common';
import useHistoryState from '../../../../hooks/useHistoryState';
import { useUserGroupsCurrentUserAdminState } from '../../../../services/user.service';
import { useLoadDataToSnowflake } from '../../../../services/workflow.services';
import { useAutomateReportGeneration } from '../../../../services/automation.service';
import { REPORT_TYPE_IDS, reportTypesV1 } from '../consts/v1/reportTypes';
import { statusKeys } from '../../components/Funds/consts';

function addItemToArray(arr, index, newItem) {
  return [...arr.slice(0, index + 1), newItem, ...arr.slice(index + 1)];
}

const createTableMeta = (fund, periodId, isStaticTemplate) => ({
  period_id: periodId,
  fund_name: fund.fund_name,
  fund_id: fund.fund_id,
  is_template: isStaticTemplate
});

const createDefaultValues = tableSchema => {
  const values = {};
  Object.keys(tableSchema?.properties || {}).forEach(columnKey => {
    values[columnKey] = getDefaultValueByType(tableSchema.properties[columnKey]);
  });
  return values;
};

const createIndexByKey = (rows, key) => {
  const map = new Map();
  if (!Array.isArray(rows)) {
    return map;
  }

  rows.forEach(row => {
    map.set(row?.[key], row || {});
  });

  return map;
};

const getEnumValues = (tableSchema, propertyKey) =>
  tableSchema?.properties?.[propertyKey]?.rules?.array?.enum || [];

const buildRowsFromEnum = ({
  rows,
  enumValues,
  lookupKey,
  assignKey = lookupKey,
  defaults,
  meta
}) => {
  const rowByKey = createIndexByKey(rows, lookupKey);
  return enumValues.map(item => ({
    ...meta,
    ...defaults,
    ...(rowByKey.get(item.value) || {}),
    [assignKey]: item.value
  }));
};

const buildRowsFromRanks = ({ rows, ranks, defaults, meta, outputRanking = true }) => {
  const rowByRank = createIndexByKey(rows, 'ranking');
  return ranks.map(rank => ({
    ...meta,
    ...defaults,
    ...(rowByRank.get(rank) || {}),
    ranking: outputRanking ? rank : ''
  }));
};

const normalizeFundTableRows = ({ tableKey, tableSchema, rows, defaults, meta }) => {
  if (tableKey === 'TURNOVER') {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'sub_asset_class'),
      lookupKey: 'sub_asset_class',
      defaults,
      meta
    });
  }

  if (
    tableKey === 'FINANCING_BORROWING_SECURED_1' ||
    tableKey === 'FINANCING_BORROWING_SECURED_2' ||
    tableKey === 'FINANCING_BORROWING_SECURED_3' ||
    tableKey === 'FINANCING_BORROWING_UNSECURED'
  ) {
    return [1, 2, 3].map(month => {
      const valueRow = rows.find(item => item.month === month);
      return {
        ...meta,
        ...defaults,
        ...valueRow,
        month
      };
    });
  }

  if (
    tableKey === 'HEDGE_FUND_AGGREGATED_ASSETS_EXPOSURE' ||
    tableKey === 'HEDGE_FUND_EXPOSURE_TRADING'
  ) {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'sub_asset_class'),
      lookupKey: 'sub_asset_class',
      defaults,
      meta
    });
  }

  if (tableKey === 'FUND_ASSETS_AND_LIABILITIES_VALUE') {
    return buildRowsFromRanks({
      rows,
      ranks: ['1', '2'],
      defaults,
      meta,
      outputRanking: false
    });
  }

  if (tableKey === 'GROSS_NET_PERFORMANCE') {
    return buildRowsFromRanks({
      rows,
      ranks: Array.from({ length: 17 }, (_, index) => `${index + 1}`),
      defaults,
      meta
    });
  }

  if (tableKey === 'HEDGE_FUND_COLLATERAL_Q36' || tableKey === 'HEDGE_FUND_COLLATERAL_Q37') {
    return buildRowsFromRanks({
      rows,
      ranks: Array.from({ length: 5 }, (_, index) => `${index + 1}`),
      defaults,
      meta
    });
  }

  if (tableKey === 'COUNTERPARTIES_DOLLAR' || tableKey === 'COUNTERPARTIES_PERCENT') {
    return buildRowsFromRanks({
      rows,
      ranks: ['1', '2', '3', '4', '5'],
      defaults,
      meta
    });
  }

  if (tableKey === 'LIQUIDITY_FUND_ASSETS') {
    return buildRowsFromRanks({
      rows,
      ranks: ['1', '2', '3'],
      defaults,
      meta
    });
  }

  if (tableKey === 'AGGREGATED_ASSETS') {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'fund_type'),
      lookupKey: 'fund_type',
      defaults,
      meta
    });
  }

  if (tableKey === 'FINANCING_BORROWING_UNSECURED' || tableKey === 'FINANCING_BORROWING_SECURED') {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'sub_asset_class'),
      lookupKey: 'sub_asset_class',
      defaults,
      meta
    });
  }

  if (tableKey === 'NETTED_EXPOSURE_FOR_REFERENCE_ASSETS') {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'reference_type'),
      lookupKey: 'reference_type',
      defaults,
      meta
    });
  }

  if (tableKey === 'BORROWING_DETAIL') {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'depository_institutions'),
      lookupKey: 'depository_institutions',
      defaults,
      meta
    });
  }

  if (tableKey === 'BENEFICIALLY_OWNED_EQUITY_INVESTORS') {
    return buildRowsFromEnum({
      rows,
      enumValues: getEnumValues(tableSchema, 'sub_asset_type'),
      lookupKey: 'sub_asset_type',
      defaults,
      meta
    });
  }

  if (tableKey === 'RISK_METRICS_BY_MARKET_FACTORS') {
    const rowBySubAssetClass = createIndexByKey(rows, 'sub_asset_class');
    const assetClasses = getEnumValues(tableSchema, 'asset_class');
    const subAssetClasses = getEnumValues(tableSchema, 'sub_asset_class');

    return assetClasses
      .map(assetClass =>
        subAssetClasses
          .filter(subAssetClass => subAssetClass.value.includes(assetClass.value))
          .map(subAssetClass => ({
            ...meta,
            ...defaults,
            ...(rowBySubAssetClass.get(subAssetClass.value) || {}),
            sub_asset_class: subAssetClass.value,
            asset_class: assetClass.value
          }))
      )
      .flat();
  }

  if (Array.isArray(rows) && rows.length > 0) {
    return rows.map(row => ({
      ...meta,
      ...defaults,
      ...row
    }));
  }

  return [
    {
      ...meta,
      ...defaults,
      ...(rows?.[0] || {})
    }
  ];
};

const areObjectsShallowEqual = (left, right) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every(key => left[key] === right[key]);
};

const areRowsEqual = (leftRows, rightRows) => {
  if (leftRows === rightRows) {
    return true;
  }

  if (!Array.isArray(leftRows) || !Array.isArray(rightRows)) {
    return false;
  }

  if (leftRows.length !== rightRows.length) {
    return false;
  }

  for (let index = 0; index < leftRows.length; index += 1) {
    if (!areObjectsShallowEqual(leftRows[index], rightRows[index])) {
      return false;
    }
  }

  return true;
};

export const useReport = ({
  forceUpdate,
  isStaticTemplate,
  period,
  periodId,
  setSearchParams,
  isMultiFunds
}) => {
  const { reportingMemberState } = useParams();
  const [searchParams] = useSearchParams();
  const jurisdictionId = searchParams.get('jurisdictionId');
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const currentReportType = searchParams.get('type') || reportTypesV1[0].id;
  const currentReportSections = reportTypesV1.find(item => item.id === currentReportType)?.sections;
  const currentSection = searchParams.get('section') || currentReportSections[0].id;
  const currentFundId = searchParams.get('fund_id') || null;

  const [previousFunds, setPreviousFunds] = useState({});
  const [schema, setSchema] = useState({});

  const [hasChanges, setHasChanges] = useState(false);
  const [pendingFundId, setPendingFundId] = useState(null);

  const { data: AIFMjurisdictions } = useGetAIFMJurisdictions({
    periodId,
    queryParams: {
      enabled: !!period && !!jurisdictionId
    }
  });

  const currentPeriod = AIFMjurisdictions?.find(
    item => item.reporting_member_state === reportingMemberState.toUpperCase()
  );

  const { data: reportJurisdictions } = useGetJurisdictions({
    periodId,
    queryParams: {
      enabled: !!period && !jurisdictionId
    }
  });

  const jurisdictions = jurisdictionId ? AIFMjurisdictions : reportJurisdictions;

  // Fetch FormPF jurisdiction to get required_sections per fund.
  const { data: formPFJurisdiction } = useGetFormPFJurisdiction({
    periodId,
    queryParams: {
      enabled: !!periodId && !jurisdictionId
    }
  });

  const fundRequiredSectionsMap = useMemo(() => {
    const map = {};
    for (const jFund of formPFJurisdiction?.funds || []) {
      const sections = (jFund.sections || []).map(s => s.section_key).filter(Boolean);
      map[String(jFund.fund_id)] = Array.from(new Set(['1b', ...sections]));
    }
    return map;
  }, [formPFJurisdiction]);

  const {
    state: { funds },
    set,
    undo,
    setInitialState,
    redo,
    canRedo,
    canUndo
  } = useHistoryState({
    funds: {}
  });

  const { data: user, isLoading: isLoadingUser } = useUserGroupsCurrentUserAdminState();

  const isViewer = useMemo(() => {
    if (user?.role === 'Approver') {
      commonStore.setIsViewer(true);
      return true;
    }
    return false;
  }, [user]);

  const onChangeFundId = useCallback(
    fundId => {
      if (hasChanges) {
        setPendingFundId(fundId);
        return;
      }

      setSearchParams(prev => {
        prev.set('fund_id', fundId);
        return prev;
      });
    },
    [setSearchParams, hasChanges]
  );

  const onSaveResult = result => {
    let res = currentReportType === REPORT_TYPE_IDS.AIF001 ? [result] : result;

    if (isComparisonMode) {
      res = res.map(item => ({
        ...item.current_fund,
        previous_fund: item.previous_fund,
        difference: item.difference
      }));
    }

    if (currentReportType === REPORT_TYPE_IDS.AIF001 || jurisdictionId) {
      setSearchParams(prev => {
        prev.set('fund_id', REPORT_TYPE_IDS.AIF001);
        return prev;
      });
    } else {
      if (currentFundId === REPORT_TYPE_IDS.AIF001 || !currentFundId) {
        setSearchParams(prev => {
          prev.set('fund_id', res[0]?.fund_id);
          return prev;
        });
      }
    }

    const _funds = res.reduce((acc, item) => {
      const fundKey =
        currentReportType === REPORT_TYPE_IDS.AIF001 ? REPORT_TYPE_IDS.AIF001 : item.fund_id;

      if (!isStaticTemplate) {
        const requiredTables = {};
        (item.required_tables || []).forEach(table => {
          requiredTables[table.table_name] = table;
        });

        acc[fundKey] = {
          ...item,
          required_tables: requiredTables
        };
      } else {
        acc[fundKey] = { ...item };
      }

      return acc;
    }, {});

    Object.keys(_funds).forEach(fundKey => {
      const fund = _funds[fundKey];
      const tableNames = Object.keys(fund.fund_data || {});
      fund.isSelected = true;
      tableNames.forEach(tableKey => {
        const tableSchema = schema[tableKey];
        if (!tableSchema) {
          return;
        }

        const defaults = createDefaultValues(tableSchema);
        const meta = createTableMeta(fund, periodId, isStaticTemplate);

        fund.fund_data[tableKey] = normalizeFundTableRows({
          tableKey,
          tableSchema,
          rows: fund.fund_data[tableKey],
          defaults,
          meta
        });
      });

      if (isComparisonMode && fund.difference) {
        delete fund.difference;
      }
    });

    commonStore.setFundsSize(Object.keys(_funds).length);

    setInitialState({
      funds: _funds
    });

    setPreviousFunds(_funds);

    toast.remove();
  };

  const report = useGetLoadAllData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      report_type: currentReportType,
      reporting_member_state: reportingMemberState.toUpperCase(),
      comparison_mode: isComparisonMode
    },
    queryParams: {
      enabled:
        !!(Object.keys(schema).length > 0) &&
        !isViewer &&
        !(
          period.data?.modeling_status === 'Not started' &&
          period.data?.report_progress_status === 'Not started'
        ) &&
        !jurisdictionId,
      onSuccess: onSaveResult,
      cacheTime: 0,
      staleTime: 0
    }
  });

  const approverReport = useGetApproverReport({
    periodId,
    params: {
      force_update: forceUpdate,
      is_template: isStaticTemplate,
      report_type: currentReportType,
      reporting_member_state: reportingMemberState.toUpperCase(),
      comparison_mode: isComparisonMode
    },
    queryParams: {
      enabled: !!(Object.keys(schema).length > 0) && isViewer,
      onSuccess: onSaveResult,
      cacheTime: 0,
      staleTime: 0
    }
  });

  const aifmReport = useGetAIFMReport({
    periodId,
    params: {
      jurisdiction_id: jurisdictionId,
      is_template: isStaticTemplate
    },
    queryParams: {
      onSuccess: onSaveResult,
      enabled: !!(Object.keys(schema).length > 0) && !!jurisdictionId && !isViewer
    }
  });

  const isLoading = isViewer
    ? approverReport.isLoading
    : jurisdictionId
      ? aifmReport.isLoading
      : report.isLoading;

  const refetch = isViewer
    ? approverReport.refetch
    : jurisdictionId
      ? aifmReport.refetch
      : report.refetch;

  const reportLoadError = isViewer
    ? approverReport.error
    : jurisdictionId
      ? aifmReport.error
      : report.error;
  const isReportLoadError = isViewer
    ? approverReport.isError
    : jurisdictionId
      ? aifmReport.isError
      : report.isError;

  const loadDataToSnowflake = useLoadDataToSnowflake();
  const automateReportGeneration = useAutomateReportGeneration();

  useEffect(() => {
    if (
      period.data?.modeling_status === 'Not started' &&
      period.data?.report_progress_status === 'Not started' &&
      !isViewer &&
      !isStaticTemplate
    ) {
      // Initial periods (template_type === 'sratch') go through the
      // load-data-to-snowflake pipeline; automation periods use the
      // automation-report endpoint.
      const isInitialPeriod = period.data?.template_type === 'sratch';
      const promise = isInitialPeriod
        ? loadDataToSnowflake.mutateAsync(periodId, {
            onSuccess: () => {
              refetch();
              period.refetch();
            }
          })
        : automateReportGeneration.mutateAsync(
            { period_id: periodId },
            {
              onSuccess: () => {
                refetch();
                period.refetch();
              }
            }
          );
      toast.promise(promise, {
        loading: 'Generating report...',
        success: 'Рeport was successfully generated',
        error: 'Something was wrong while generating report'
      });
    }
  }, [period.data, isStaticTemplate]);

  const { mutateAsync: mutateAsyncReport, isLoading: isLoadingDataReport } = useUpdateData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      reporting_member_state: reportingMemberState.toUpperCase()
    }
  });

  const { mutateAsync: mutateAsyncAIFM, isLoading: isLoadingDataAIFM } = useAIFMUpdateData({
    periodId,
    params: {
      is_template: isStaticTemplate,
      jurisdiction_id: jurisdictionId
    }
  });

  const mutateAsync = jurisdictionId ? mutateAsyncAIFM : mutateAsyncReport;
  const isLoadingData = jurisdictionId ? isLoadingDataAIFM : isLoadingDataReport;

  const { isLoading: isLoadingSchema } = useGetReportSchema({
    periodId: jurisdictionId ? currentPeriod?.period_id : periodId,
    params: {
      reporting_member_state: reportingMemberState.toUpperCase(),
      isStaticTemplate: isStaticTemplate
    },
    queryParams: {
      cacheTime: 0,
      staleTime: 0,
      enabled: jurisdictionId ? !!currentPeriod : !!periodId,
      onSuccess: res => {
        setSchema(res);
      }
    }
  });

  const onChangeFund = useCallback(
    (key, value, fundId) => {
      // Hot path: avoid pushing full history snapshot on each keystroke.
      set(prevState => {
        const targetId = fundId || currentFundId;
        const prevFunds = prevState?.funds || {};
        const fund = prevFunds[targetId];
        if (!fund) return prevState;
        if (fund.fund_data?.[key] === value) return prevState;

        return {
          funds: {
            ...prevFunds,
            [targetId]: {
              ...fund,
              fund_data: {
                ...fund.fund_data,
                [key]: value
              }
            }
          }
        };
      }, false);
    },
    [currentFundId, set]
  );

  const addRow = useCallback(
    (key, index) => {
      const propsSchema = schema[key]?.properties;
      if (!propsSchema) return;

      const nextFunds = { ...funds };

      Object.keys(funds).forEach(fundId => {
        const fund = funds[fundId];
        const refFundId = isMultiFunds ? fundId : currentFundId;
        const refFund = funds[refFundId];
        if (!fund || !refFund) return;

        const _values = {};
        Object.keys(propsSchema).forEach(columnKey => {
          _values[columnKey] = getDefaultValueByType(propsSchema[columnKey]);
        });

        const currentTable = Array.isArray(fund.fund_data[key]) ? fund.fund_data[key] : [];

        nextFunds[fundId] = {
          ...fund,
          fund_data: {
            ...fund.fund_data,
            [key]: addItemToArray(currentTable, index, {
              ..._values,
              period_id: periodId,
              fund_name: refFund.fund_name,
              fund_id: refFund.fund_id
            })
          }
        };
      });

      set({
        funds: nextFunds
      });
    },
    [funds, schema, isMultiFunds, currentFundId, periodId, set]
  );

  const onDeleteRow = useCallback(
    (key, index) => {
      const nextFunds = { ...funds };

      Object.keys(funds).forEach(fundId => {
        const fund = funds[fundId];
        const table = fund.fund_data[key];
        if (!Array.isArray(table)) return;

        nextFunds[fundId] = {
          ...fund,
          fund_data: {
            ...fund.fund_data,
            [key]: table.map((item, ind) =>
              ind === index
                ? {
                    ...item,
                    is_deleted: true
                  }
                : item
            )
          }
        };
      });

      set({
        funds: nextFunds
      });
    },
    [funds, set]
  );

  const onClearStaticData = useCallback(() => {
    try {
      set({ funds: structuredClone(funds) });
    } catch {
      set({ funds: JSON.parse(JSON.stringify(funds)) });
    }
  }, [funds, set]);

  const getFieldsToFilter = useCallback((tableName, tableSchema) => {
    if (!tableSchema?.properties) {
      return [];
    }

    if (tableName === 'AGGREGATED_ASSETS') {
      return Object.keys(tableSchema.properties).filter(key => key !== 'fundType');
    }

    const excludedKeys = [
      'asset_type',
      'macro_asset_type',
      'transaction_type',
      'risk_measure_type',
      'macro_asset_type_label',
      'sub_asset_type_label',
      'asset_type_label',
      'sub_asset_class',
      'relevant_type_cd'
    ];

    return Object.keys(tableSchema.properties).filter(key => !excludedKeys.includes(key));
  }, []);

  const processTableData = useCallback((fund, tableName, fields) => {
    if (!fund || !Array.isArray(fund)) {
      return null;
    }

    let processedFund = fund;

    if (tableName === 'FUND_ASSETS_AND_LIABILITIES_VALUE') {
      processedFund = fund.map((item, i) => ({
        ...item,
        ranking: `${i + 1}`
      }));
    }

    return filterArrayByFieldsNotEmptyValue(processedFund, fields);
  }, []);

  const isFilteredTableChanged = useCallback((currentFiltered, previousFiltered) => {
    if (!previousFiltered || !Array.isArray(previousFiltered)) {
      return currentFiltered && currentFiltered.length > 0;
    }

    if (!currentFiltered || !Array.isArray(currentFiltered)) {
      return previousFiltered.length > 0;
    }

    if (currentFiltered.length !== previousFiltered.length) {
      return true;
    }

    return !areRowsEqual(currentFiltered, previousFiltered);
  }, []);

  const processTableForSave = useCallback(
    (tableName, currentTable, previousTable) => {
      const tableSchema = schema[tableName];
      if (!tableSchema) {
        return null;
      }

      const fields = getFieldsToFilter(tableName, tableSchema);
      const currentFiltered = processTableData(currentTable, tableName, fields);

      if (!currentFiltered) {
        return null;
      }

      const previousFiltered = previousTable
        ? processTableData(previousTable, tableName, fields)
        : null;

      if (isFilteredTableChanged(currentFiltered, previousFiltered)) {
        return currentFiltered;
      }

      return null;
    },
    [schema, getFieldsToFilter, processTableData, isFilteredTableChanged]
  );

  const getChangedFundData = useCallback(
    (currentFund, previousFund) => {
      if (!currentFund?.fund_data) {
        return {};
      }

      const changedFundData = {};
      const tableNames = Object.keys(currentFund.fund_data);

      tableNames.forEach(tableName => {
        const currentTable = currentFund.fund_data[tableName];
        const previousTable = previousFund?.fund_data?.[tableName];

        const filteredData = processTableForSave(tableName, currentTable, previousTable);
        if (filteredData) {
          changedFundData[tableName] = filteredData;
        }
      });

      return changedFundData;
    },
    [processTableForSave]
  );

  const checkFundForChanges = useCallback(
    (currentFund, previousFund) => {
      return Object.keys(getChangedFundData(currentFund, previousFund)).length > 0;
    },
    [getChangedFundData]
  );

  const checkForChanges = useCallback(() => {
    if (!previousFunds || Object.keys(previousFunds).length === 0) {
      return false;
    }

    if (!funds || Object.keys(funds).length === 0) {
      return false;
    }

    if (!schema || Object.keys(schema).length === 0) {
      return false;
    }

    if (isMultiFunds) {
      return Object.values(funds).some(fundValue => {
        const fundId = fundValue.fund_id;
        const currentFund = funds[fundId];
        const previousFund = previousFunds[fundId];

        return currentFund && checkFundForChanges(currentFund, previousFund);
      });
    }

    if (!currentFundId) {
      return false;
    }

    const currentFund = funds[currentFundId];
    const previousFund = previousFunds[currentFundId];

    return checkFundForChanges(currentFund, previousFund);
  }, [funds, previousFunds, schema, currentFundId, isMultiFunds, checkFundForChanges]);

  useEffect(() => {
    const hasActualChanges = checkForChanges();
    setHasChanges(hasActualChanges);
  }, [checkForChanges]);

  const revertCurrentFundChanges = useCallback(() => {
    if (!hasChanges) return;
    if (!currentFundId || !previousFunds[currentFundId]) return;

    const nextFunds = {
      ...funds,
      [currentFundId]: previousFunds[currentFundId]
    };

    set({
      funds: nextFunds
    });
    setHasChanges(false);
  }, [hasChanges, currentFundId, previousFunds, funds, set]);

  const onSave = ({ callbackFn } = {}) => {
    const payload = [];

    if (isMultiFunds) {
      Object.values(funds).forEach(fundValue => {
        const fundId = fundValue.fund_id;
        const currentFund = funds[fundId];
        const previousFund = previousFunds[fundId];

        if (!currentFund) {
          return;
        }

        const fundData = getChangedFundData(currentFund, previousFund);

        if (Object.keys(fundData).length > 0) {
          payload.push({
            fund_id: fundValue.fund_id,
            fund_name: fundValue.fund_name,
            fund_data: fundData
          });
        }
      });
    } else {
      const fundId = currentFundId;
      const currentFund = funds[fundId];
      const previousFund = previousFunds[fundId];

      if (!currentFund) {
        if (callbackFn) callbackFn();
        return;
      }

      const fundData = getChangedFundData(currentFund, previousFund);

      if (Object.keys(fundData).length === 0) {
        if (callbackFn) callbackFn();
        return;
      }

      payload.push({
        fund_id: funds[fundId].fund_id,
        fund_name: funds[fundId].fund_name,
        fund_data: fundData
      });
    }

    if (payload.length === 0) {
      if (callbackFn) callbackFn();
      return;
    }

    toast.promise(mutateAsync(payload), {
      loading: 'Saving...',
      success: () => {
        if (!isStaticTemplate) toast.loading('Validating...');
        async function loadData() {
          const result = await refetch();
          setHasChanges(false);
          if (result.isSuccess && callbackFn) callbackFn();
        }
        loadData();

        return 'Saved successfuly!';
      },
      error: 'Error on saving report!'
    });
  };

  const [pendingReportTypeId, setPendingReportTypeId] = useState(null);

  const applyReportTypeChange = useCallback(
    id => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('type', id);
        return next;
      });
      setPendingReportTypeId(null);
    },
    [setSearchParams]
  );

  const clearPendingReportTypeId = useCallback(() => {
    setPendingReportTypeId(null);
  }, []);

  const applyFundChange = useCallback(
    id => {
      setSearchParams(prev => {
        prev.set('fund_id', id);
        return prev;
      });
      setPendingFundId(null);
    },
    [setSearchParams]
  );

  const clearPendingFundId = useCallback(() => {
    setPendingFundId(null);
  }, []);

  const onChangeReportType = useCallback(
    id => {
      if (!hasChanges) {
        applyReportTypeChange(id);
        return;
      }
      setPendingReportTypeId(id);
    },
    [hasChanges, applyReportTypeChange]
  );

  const fundsWithStatus = useMemo(() => {
    if (funds) {
      let errors = Object.values(funds).filter(
        item => item.report_status?.submit_status === statusKeys.ERROR
      );

      let submited = Object.values(funds).filter(
        item => item.report_status?.submit_status === statusKeys.SUBMITTED
      );

      let readyForSubmit = Object.values(funds).filter(
        item => item.report_status?.submit_status === statusKeys.READY
      );

      return {
        errors,
        submited,
        readyForSubmit
      };
    }

    return {
      errors: [],
      submited: [],
      readyForSubmit: []
    };
  }, [funds]);

  const onChangeFundSelection = fundId => {
    if (fundId === 'all') {
      const fundIds = Object.keys(funds);
      const isAllSelected = fundIds.every(id => funds[id].isSelected);
      const nextFunds = fundIds.reduce((acc, id) => {
        acc[id] = {
          ...funds[id],
          isSelected: isAllSelected ? id === currentFundId : true
        };
        return acc;
      }, {});
      setInitialState({ funds: nextFunds });
      return;
    }

    const targetFund = funds[fundId];
    if (!targetFund) return;

    const selectedFundList = Object.values(funds).filter(fund => fund.isSelected);
    const isDeselecting = targetFund.isSelected;
    const isCurrentFund = fundId === currentFundId;
    const isLastSelectedFund = isDeselecting && selectedFundList.length === 1;

    if (isLastSelectedFund) return;

    if (isDeselecting && isCurrentFund) {
      const nextFund = selectedFundList.find(fund => fund.fund_id !== fundId);
      if (nextFund) {
        setSearchParams(
          prev => {
            prev.set('fund_id', nextFund.fund_id);
            return prev;
          },
          { replace: true }
        );
      }
    }

    setInitialState({
      funds: {
        ...funds,
        [fundId]: {
          ...targetFund,
          isSelected: !targetFund.isSelected
        }
      }
    });
  };

  const selectedFundsById = Object.values(funds)
    .filter(fund => fund.isSelected)
    .reduce((acc, fund) => {
      acc[fund.fund_id] = fund;
      return acc;
    }, {});

  const rawVisibleFunds = currentReportType === REPORT_TYPE_IDS.AIF001 ? funds : selectedFundsById;

  const managerRequiredSections = useMemo(() => {
    const sections = formPFJurisdiction?.manager_sections;
    if (!Array.isArray(sections) || sections.length === 0) {
      return ['1a'];
    }
    return Array.from(new Set(['1a', ...sections]));
  }, [formPFJurisdiction?.manager_sections]);

  // Attach required_sections from the jurisdiction so MultiFundRow can grey out
  // columns for funds that don't require the currently-displayed section.
  const visibleFunds = useMemo(() => {
    if (currentReportType === REPORT_TYPE_IDS.AIF001) {
      const result = {};
      for (const [key, fund] of Object.entries(rawVisibleFunds)) {
        result[key] = { ...fund, required_sections: managerRequiredSections };
      }
      return result;
    }
    if (!Object.keys(fundRequiredSectionsMap).length) return rawVisibleFunds;
    const result = {};
    for (const [key, fund] of Object.entries(rawVisibleFunds)) {
      const sections = fundRequiredSectionsMap[String(fund.fund_id)];
      result[key] = sections ? { ...fund, required_sections: sections } : fund;
    }
    return result;
  }, [rawVisibleFunds, fundRequiredSectionsMap, currentReportType, managerRequiredSections]);

  const toggleComparisonMode = useCallback(() => {
    setIsComparisonMode(prev => !prev);
  }, []);

  useEffect(() => {
    setIsComparisonMode(false);
  }, [currentReportType]);

  return {
    funds: visibleFunds,
    allFunds: funds,
    onChangeFundSelection,
    schema,
    isLoading: loadDataToSnowflake.isLoading || isLoading,
    hasChanges,
    currentDataKey: currentFundId,
    isLoadingData,
    isLoadingSchema,
    isLoadingUser,
    isViewer,
    jurisdictions,
    previousFunds,
    addRow,
    onSave,
    refetch,
    onDeleteRow,
    onChangeFund,
    onChangeDataKey: onChangeFundId,
    onClearStaticData,
    canRedo,
    canUndo,
    undo,
    redo,
    currentSection,
    currentReportType,
    onChangeReportType,
    pendingReportTypeId,
    clearPendingReportTypeId,
    applyReportTypeChange,
    pendingFundId,
    clearPendingFundId,
    applyFundChange,
    fundsWithStatus,
    revertCurrentFundChanges,
    managerRequiredSections,
    isComparisonMode,
    toggleComparisonMode,
    reportLoadError,
    isReportLoadError
  };
};
